import { createSlice } from '@reduxjs/toolkit';
import { fetchSerialNumber, sendSerialNumberRecoveryEmail, populateRenewal } from 'software_renewal/redux/thunks';
import { calculateDiscount, setShippingOptionMetadata } from 'software_renewal/redux/utils';
import { doAddToCart } from 'shared/thunks';

const softwareRenewalSlice = createSlice({
  name: 'softwareRenewal',
  initialState: {
    licenseInfo: null,
    error: null,
    multipleProductsError: null,
    loading: false,
    serialNumberLoaded: false,
    serialNumber: '',
    addingToCart: false,
    showForgotSerialNumberModal: false,
    includeShipping: {},
  },
  reducers: {
    setSerialNumber: (state, action) => {
      state.serialNumber = action.payload;
    },
    resetSerialNumber: (state) => {
      state.serialNumber = '';
      state.serialNumberLoaded = null;
      state.licenseInfo = null;
      state.includeShipping = {};
    },
    setIncludeShipping: (state, action) => {
      const { variantId, value } = action.payload;
      state.includeShipping[variantId] = value;
    },
    toggleForgotSerialNumberModal: (state, action) => {
      state.showForgotSerialNumberModal = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearMultipleProductsError: (state) => {
      state.multipleProductsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSerialNumber.pending, (state) => {
        state.loading = true;
        state.multipleProductsError = null;
      })
      .addCase(fetchSerialNumber.fulfilled, (state, action) => {
        state.loading = false;
        const {
          network_license_text,
          dongle_locked_text,
          customer_name,
          customer_email,
          expiration_date,
          serial,
          products,
          evaluation_version,
          license_span,
          complemented_by,
        } = action.payload || {};

        if (evaluation_version) {
          state.licenseInfo = {
            network_license_text,
            dongle_locked_text,
            customer_name,
            expiration_date,
            serial,
            products: products || [],
            subtotal: '$0.00',
            discount: 0,
            displayDiscount: '$0.00',
            total: 0,
            displayTotal: '$0.00',
            evaluation_version,
          };
        } else {
          // Validate products array exists and is not empty
          if (!products || !Array.isArray(products) || products.length === 0) {
            state.error = 'No products found for this serial number';
            return;
          }

          if (products.length > 1) {
            state.multipleProductsError = 'This serial number is not available for renewal. Please contact support or try a different serial number.';
            // eslint-disable-next-line no-console
            console.error('Multiple products found for serial number');
            return;
          }

          const priceInfo = calculateDiscount(products);

          const processedProducts = setShippingOptionMetadata(products);

          state.licenseInfo = {
            network_license_text,
            dongle_locked_text,
            customer_name,
            expiration_date,
            serial,
            license_span,
            evaluation_version,
            customer_email,
            complemented_by: complemented_by || null,
            products: processedProducts,
            ...priceInfo
          };
        }
        state.serialNumberLoaded = serial;
      })
      .addCase(fetchSerialNumber.rejected, (state) => {
        state.loading = false;
        state.error = 'Serial not valid. Please try again or contact support.';
      })
      .addCase(doAddToCart.pending, (state) => {
        state.addingToCart = true;
      })
      .addCase(doAddToCart.fulfilled, (state) => {
        state.addingToCart = false;
      })
      .addCase(doAddToCart.rejected, (state) => {
        state.addingToCart = false;
      })
      .addCase(sendSerialNumberRecoveryEmail.pending, (state) => {
        state.sendingSerialNumberRecoveryEmail = true;
        state.loading = true;
      })
      .addCase(sendSerialNumberRecoveryEmail.fulfilled, (state) => {
        state.sendingSerialNumberRecoveryEmail = false;
        state.error = null;
        state.loading = false;
      })
      .addCase(sendSerialNumberRecoveryEmail.rejected, (state, action) => {
        state.sendingSerialNumberRecoveryEmail = false;
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(populateRenewal.pending, (state) => {
        state.loading = true;
      })
      .addCase(populateRenewal.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(populateRenewal.rejected, (state) => {
        state.loading = false;
        state.error = 'An error occurred while trying to renew the license';
      });
  },
});

export const {
  setSerialNumber,
  resetSerialNumber,
  setIncludeShipping,
  toggleForgotSerialNumberModal,
  clearError,
  clearMultipleProductsError,
} = softwareRenewalSlice.actions;

export default softwareRenewalSlice.reducer;
